/**
 * RequireAdmin — a reusable route guard for the Configuration surface.
 *
 * Renders the nested route (`<Outlet/>`) only when the signed-in operator holds
 * the admin role (`operator.isAdmin`, derived from the JWT in `AuthContext`) AND
 * the console is NOT in Front Desk view; otherwise it renders the
 * `PermissionDenied` panel inside the shell. It is a presentation-only gate over
 * the Operations/Configuration split — the dink-api is the real authority and
 * enforces the same on every config endpoint.
 *
 * Front Desk view (DNKADM-0031) is a UI gate on the admin's own session: while it
 * is active, Configuration is hidden from the nav AND a direct-URL nav to a config
 * route redirects home (`HOME_PATH`, DNKADM-0288 — the operator must exit Front
 * Desk view — password re-check — to reach Configuration again).
 *
 * This guards Configuration against a non-admin **operator** — OWNER/ADMIN vs.
 * STAFF and similar roles that are still staff. It is a different question from
 * "is this principal staff at all," which member (`USER`-role) principals now
 * fail: that broader gate lives above the whole shell in `AuthGate`
 * (DNKADM-0351), so a member never reaches this guard, or any Configuration or
 * Operations screen, in the first place.
 */
import { Navigate, Outlet } from 'react-router-dom';
import { useCurrentOperator } from '@/auth/authHooks';
import { useFrontDeskView } from '@/auth/frontDeskHooks';
import { HOME_PATH } from '@/routes/home';
import { PermissionDenied } from './PermissionDenied';
import { canOpenAdminRoutes } from './requireAdminAccess';

export function RequireAdmin() {
  const operator = useCurrentOperator();
  const { frontDeskView } = useFrontDeskView();
  if (canOpenAdminRoutes(operator?.isAdmin === true, frontDeskView)) return <Outlet />;
  // In Front Desk view, Configuration is out of reach — a direct-URL nav bounces
  // home rather than showing the (hidden) config surface.
  if (frontDeskView) return <Navigate to={HOME_PATH} replace />;
  return <PermissionDenied />;
}
